import React from "react";

import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import { Tooltip } from "@impero-com/ui";
import { Menu, Layout } from "antd";
import { ItemType } from "rc-menu/es/interface";
import { Link } from "react-router-dom";

import { DisabledState } from "../../componentTypes/disabled";
import { assertNever } from "../../utils/obj";

import * as theme from "./theme.css";

class LeftSideMenu extends React.PureComponent<
    LeftSideMenuProps,
    LeftSideMenuState
> {
    state = {
        collapsed: false,
    };

    constructor(props: LeftSideMenuProps) {
        super(props);

        if (props.collapsed !== undefined) {
            this.state.collapsed = props.collapsed;
        }
    }

    render = () => {
        const { items, selectedItemId } = this.props;
        const collapsed = this.props.collapsed ?? this.state.collapsed;

        let openIds;
        const selectedBranch = this.findItemBranchById(selectedItemId);
        if (selectedBranch) {
            if (selectedBranch.length <= 1) {
                openIds = items.map((item) => item.id);
            } else {
                openIds = selectedBranch.map((item) => item.id);
            }
        }

        const iconProps = {
            className: theme.leftSideMenuToggle,
            onClick: () => {
                const newCollapsed = !collapsed;
                this.setState({ collapsed: newCollapsed });
                if (this.props.onCollapsedChange) {
                    this.props.onCollapsedChange(newCollapsed);
                }
            },
        };
        const LeftIcon = <CaretLeftOutlined {...iconProps} />;
        const RightIcon = <CaretRightOutlined {...iconProps} />;

        return (
            <Layout.Sider
                className={theme.leftSideMenuSider}
                collapsible={true}
                collapsedWidth={24}
                trigger={null}
                collapsed={collapsed}
                width={250}
            >
                <div className={theme.leftSideMenuToggleContainer}>
                    {collapsed ? RightIcon : LeftIcon}
                </div>
                {!collapsed ? (
                    <Menu
                        id="left-side-menu"
                        mode="inline"
                        className={theme.leftSideMenu}
                        selectedKeys={[selectedItemId]}
                        defaultOpenKeys={openIds}
                        onClick={({ keyPath }) => {
                            const clickedItem = this.findItemByPath(keyPath);
                            if (
                                clickedItem &&
                                clickedItem.type === "SimpleMenuItem" &&
                                clickedItem.handler.type === "CLICK"
                            ) {
                                clickedItem.handler.onClick();
                            }
                        }}
                        items={items.map((item) => this.mapItemToMenu(item, 0))}
                    />
                ) : null}
            </Layout.Sider>
        );
    };

    private mapItemToMenu = (item: MenuItemSpec, depth: number): ItemType => {
        switch (item.type) {
            case "SimpleMenuItem": {
                const { disabled } = item;
                let itemContent;
                switch (item.handler.type) {
                    case "HTML":
                        itemContent = (
                            <a href={item.handler.href}>{item.label}</a>
                        );
                        break;
                    case "CLICK":
                        itemContent = item.label;
                        break;
                    case "ROUTER":
                        itemContent = (
                            <Link to={item.handler.linkTo}>{item.label}</Link>
                        );
                        break;
                    default:
                        return assertNever(item.handler);
                }
                return {
                    key: item.id,
                    disabled: !!disabled,
                    label:
                        disabled && disabled.type === "DisabledWithReason" ? (
                            <Tooltip placement="bottom" title={disabled.reason}>
                                {itemContent}
                            </Tooltip>
                        ) : (
                            itemContent
                        ),
                };
            }
            case "SubMenuItem": {
                return {
                    key: item.id,
                    label: item.label,
                    children: item.childItems.map((item) =>
                        this.mapItemToMenu(item, depth + 1)
                    ),
                };
            }
            default: {
                return assertNever(item);
            }
        }
    };

    private findItemBranchById = (
        id: string
    ): Array<MenuItemSpec> | undefined => {
        return this.findItemBranchByIdInternal(id, this.props.items);
    };

    private findItemBranchByIdInternal = (
        id: string,
        items: Array<MenuItemSpec>
    ): Array<MenuItemSpec> | undefined => {
        for (const item of items) {
            if (item.id === id) {
                return [item];
            } else if (item.type === "SubMenuItem") {
                const subBranch = this.findItemBranchByIdInternal(
                    id,
                    item.childItems
                );
                if (subBranch) {
                    return ([item] as Array<MenuItemSpec>).concat(subBranch);
                }
            }
        }
        return undefined;
    };

    private findItemByPath = (
        idPath: Array<string>
    ): MenuItemSpec | undefined => {
        let { items } = this.props;
        let end = idPath.length;
        while (end > 0) {
            const id = idPath[end - 1];
            const item = items.find((item) => item.id === id);
            if (!item) {
                console.error(
                    `Could not find item ${id} in [${idPath.join(", ")}]`
                );
                return undefined;
            }
            if (end <= 1) {
                return item;
            }
            end -= 1;
            if (item.type === "SubMenuItem") {
                items = item.childItems;
            } else {
                console.error(`Expected sub menu item ${id}`);
                return undefined;
            }
        }
        console.error(`Could not find item [${idPath.join(", ")}]`);
        return undefined;
    };
}

interface LeftSideMenuProps {
    items: Array<MenuItemSpec>;
    selectedItemId: string;
    onItemSelected: (itemId: string) => void;
    collapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
}

type LocationDescriptor =
    | string
    | { pathname: string; search: string; hash: string; state: unknown };
type MenuItemSpec = SimpleMenuItemSpec | SubMenuItemSpec;
type SimpleMenuItemHandler =
    | { type: "CLICK"; onClick: () => void }
    | { type: "HTML"; href: string }
    | { type: "ROUTER"; linkTo: LocationDescriptor };

interface SimpleMenuItemSpec {
    type: "SimpleMenuItem";
    id: string;
    label: React.ReactNode;
    disabled?: DisabledState;
    handler: SimpleMenuItemHandler;
}

interface SubMenuItemSpec {
    type: "SubMenuItem";
    id: string;
    label: React.ReactNode;
    childItems: Array<MenuItemSpec>;
}

interface LeftSideMenuState {
    collapsed: boolean;
}

export { LeftSideMenu, type LeftSideMenuProps, type MenuItemSpec };
